import React, { useState, useEffect } from 'react';
import { Search, Mail, Calendar, Clock } from 'lucide-react';
import { Input } from '../ui/input';
import { adminSubscribersAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';
import { formatDateTimeLocal } from '../../utils/dateFormat';
import StatusFilterTabs from './StatusFilterTabs';
import PromoterBadge from './PromoterBadge';

const SubscribersManagement = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 50,
    total: 0,
  });

  useEffect(() => {
    loadSubscribers(1);
  }, [searchTerm, statusFilter]);

  const loadSubscribers = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: 50,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status_filter: statusFilter }),
      };
      const response = await adminSubscribersAPI.getAll(params);
      setSubscribers(response.data);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudieron cargar los suscriptores',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const isActive = (s) =>
    s.subscription_status === 'active' || s.subscription_status === 'trialing';
  const hasPaid = (s) => !!s.paid_at;

  return (
    <div className="p-6 space-y-6">
      <Toaster />
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Suscriptores</h2>
        <p className="text-gray-500">Vista de suscriptores (solo lectura)</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Buscar por nombre o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-gray-50 border-gray-300 text-gray-900"
          />
        </div>
        <StatusFilterTabs value={statusFilter} onChange={setStatusFilter} />
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando...</div>
      ) : subscribers.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No hay suscriptores
          {statusFilter !== 'all' && ` ${statusFilter === 'active' ? 'activos' : 'inactivos'}`}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Nombre</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Teléfono</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Estado</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Fecha de alta</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Hora / Pago</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Origen / Promotor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {subscribers.map((subscriber) => (
                  <tr key={subscriber.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-gray-900 font-medium">{subscriber.name}</td>
                    <td className="px-6 py-4 text-gray-600">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 flex-shrink-0 text-gray-400" />
                        {subscriber.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{subscriber.phone || '-'}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          isActive(subscriber)
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {subscriber.subscription_status || 'Sin suscripción'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 flex-shrink-0 text-gray-400" />
                        {formatDateTimeLocal(subscriber.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {hasPaid(subscriber) ? (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 flex-shrink-0 text-gray-400" />
                          <span title="Fecha de pago">
                            {formatDateTimeLocal(subscriber.paid_at)}
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-amber-600 text-xs font-medium">No pagado</span>
                          <span className="text-gray-500 text-xs">
                            Registrado: {formatDateTimeLocal(subscriber.created_at)}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <PromoterBadge promoter={subscriber.promoter} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pagination.last_page > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="text-gray-500 text-sm">
            Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a{' '}
            {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de{' '}
            {pagination.total}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => loadSubscribers(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
            >
              Anterior
            </button>
            <button
              onClick={() => loadSubscribers(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscribersManagement;
